import '../styles/globals.css';
import FlagProvider, {
  InMemoryStorageProvider,
} from '@unleash/proxy-client-react';
import { mergeDeepRight } from 'ramda';
import React, { useState, useEffect, useRef } from 'react';
import type { AppProps } from 'next/app';
import { ThemeProvider } from 'styled-components';
import getTheme from '../src/theme';
import useWidgetContext from '../src/hooks/useWidgetContext';
import WidgetContext, { IWidgetContext } from '../src/context/WidgetContext';
import windowConfigToWidgetContext from '../src/utils/windowConfigToWidgetContext';
import Layout from '../src/components/Layout';
import useMessaging from '../src/hooks/useMessaging';
import { HostInfo, WindowConfig } from '../src/shared/types';
import StateProvider from '../src/components/StateProvider';
import HeightContext from '../src/context/HeightContext';
import HostInfoContext from '../src/context/HostInfoContext';
import Script from 'next/script';
import useSetupAnalytics from '../src/hooks/useSetupAnalytics';
import isProduction from '../src/shared/utils/isProduction';
import getSessionId from '../src/utils/getSessionId';

const App: React.FC<AppProps> = ({ Component, pageProps }) => {
  const headerRef = useRef<HTMLHRElement | null>(null);
  const footerRef = useRef<HTMLHRElement | null>(null);
  const widgetContext = useWidgetContext();

  useSetupAnalytics(widgetContext);

  // Only render app when widget is ready
  return (
    <HeightContext.Provider value={{ footerRef, headerRef }}>
      <ThemeProvider theme={getTheme(widgetContext.styleConfig)}>
        <StateProvider>
          <Layout>
            <Component {...pageProps} />
          </Layout>
        </StateProvider>
      </ThemeProvider>
    </HeightContext.Provider>
  );
};

const WithConfig: React.FC<AppProps & { children: React.ReactNode }> = ({
  children,
  ...props
}) => {
  const [widgetContext, setWidgetContext] = useState<IWidgetContext | null>(
    null,
  );
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);
  const [sessionId, setSessionId] = useState<null | string>(null);
  const storageProvider = useRef(new InMemoryStorageProvider());

  const sendMessage = useMessaging(message => {
    if (message.type === 'overwriteConfig') {
      // Only needed for TS, the config object should be there.
      const newConfig =
        window.__DH_CONFIG != null
          ? (mergeDeepRight<WindowConfig, Partial<WindowConfig>>(
              window.__DH_CONFIG,
              message.payload,
            ) as WindowConfig)
          : window.__DH_CONFIG;

      // Also set on window
      window.__DH_CONFIG = newConfig;

      setWidgetContext(
        windowConfigToWidgetContext(newConfig, widgetContext?.hostPageParams),
      );
    }
    if (message.type === 'hostInfo') {
      setHostInfo(message.payload);
    }
  });

  useEffect(() => {
    const search = global.window.location.search;
    setWidgetContext(windowConfigToWidgetContext(window.__DH_CONFIG, search));

    // eslint-disable-next-line no-console
    if (window.__DH_CONFIG?.apps.length === 0 && console && console.warn) {
      // eslint-disable-next-line no-console
      console.warn(
        `[DatHuis Widget] No apps enabled, please enable some apps.`,
      );
    }
  }, []);

  useEffect(() => {
    if (widgetContext !== null) {
      sendMessage({
        type: 'configLoaded',
        payload: window.__DH_CONFIG,
      });
    }
  }, [sendMessage, widgetContext]);

  useEffect(() => {
    const sessionId = getSessionId();
    setSessionId(sessionId);
  }, []);

  if (
    widgetContext === null ||
    Object.keys(widgetContext?.apps).length === 0 ||
    !sessionId
  )
    return null;

  return (
    <FlagProvider
      config={{
        storageProvider: storageProvider.current,
        url: process.env.NEXT_PUBLIC_UNLEASH_URL as string,
        clientKey: process.env.NEXT_PUBLIC_UNLEASH_SECRET as string,
        appName: 'umbrella-widget',
        // env.STAGE_NAME for production is set to `prod` however that does not match the name of the Gitlab environment
        environment: isProduction()
          ? 'production'
          : process.env.NEXT_PUBLIC_STAGE_NAME,
        disableRefresh: true,
        context: {
          sessionId,
          // We set the userId to the accountId so we roll out features for certain accounts.
          // but unleash doesn't provide any other way to do this so now users are account for us.
          userId: widgetContext.accountId,
        },
      }}
    >
      <HostInfoContext.Provider value={hostInfo}>
        <WidgetContext.Provider value={widgetContext}>
          <App {...props} />
          {children}
          {/* Load Google Analytics script */}
          <Script
            id="loadGtag"
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GA_ID}`}
          />
          <Script id="initGA" strategy="afterInteractive">
            {`
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}

gtag('js', new Date());
`}
          </Script>
        </WidgetContext.Provider>
      </HostInfoContext.Provider>
    </FlagProvider>
  );
};

export default WithConfig;
